import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { TranslateOrDefault } from '@language';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { toFormSelectOption } from '../../../form-controls/helpers/form-select-option-helper';
import { multiFormSelectOptionWrapper } from '../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { ApiQuery, Restriction } from '../../api/entities/api-query.entity';
import { BaseNestedFieldDefinition, FieldType } from '../../field-definitions/entities/field-definition.entity';
import { DottedKeyToFlatKey } from '../../field-definitions/helpers/dottedkey-to-flatkey.helper';
import { ShAbstractHttpService } from '../../http/services/http.abstract.service';
import { EmptyStructuredQuery } from '../../query/entities/empty-structured-query.constant';
import { ApiStructuredQuery } from '../../query/entities/structured-query.entity';
import { Family } from '../../records/entities/metadata/metadata.entity';
import { ShRecordRestrictionService } from '../../records/services/restrictions/record-restriction.service';
import { AutocompleteRequest } from '../entities/autocomplete-request.entity';
import { AutocompleteSuggestion } from '../entities/autocomplete-suggestion.entity';

@Injectable({ providedIn: 'root' })
export class ShAutocompleteService {
    protected readonly ENDPOINT: string = `v2/autocomplete/`;

    private readonly translateService: TranslateService = inject(TranslateService);
    private recordRestrictionService: ShRecordRestrictionService = inject(ShRecordRestrictionService);

    constructor(protected http: ShAbstractHttpService) {}

    private allowedFamilies: Family[] = [Family.Administrative, Family.Descriptive, Family.Dynamic, Family.Technical];

    public getAutocompleteSuggestions(input: string, fieldDottedKey?: string): Observable<string[]> {
        if (!input) {
            return of([]);
        }

        return this.getRestrictions().pipe(
            switchMap(restrictions => {
                const query: ApiStructuredQuery = new ApiQuery(
                    EmptyStructuredQuery,
                    restrictions
                ).toApiStructuredQuery();

                const request: AutocompleteRequest = {
                    Input: input,
                    Field: fieldDottedKey,
                    Search: {
                        StructuredQuery: query
                    }
                };

                return this.requestAutocompleteSuggestions(request).pipe(
                    map(suggestions => suggestions.map(suggestion => suggestion.Term))
                );
            })
        );
    }

    public requestAutocompleteSuggestions(request: AutocompleteRequest): Observable<AutocompleteSuggestion[]> {
        return this.http.post<AutocompleteSuggestion[]>(this.ENDPOINT, request);
    }

    public getFieldAutocomplete(
        fieldDottedKey: string,
        input: string,
        translateValues: boolean = true
    ): Observable<MultiFormSelectOptions> {
        return this.getAutocompleteSuggestions(input, fieldDottedKey).pipe(
            map(results => {
                const formSelectOptions: FormSelectOption[] = results.map((result: string) =>
                    toFormSelectOption(
                        result,
                        translateValues
                            ? TranslateOrDefault(
                                  this.translateService,
                                  `overrides.${DottedKeyToFlatKey(fieldDottedKey)}.${result}`,
                                  result
                              )
                            : result
                    )
                );
                return multiFormSelectOptionWrapper(formSelectOptions);
            })
        );
    }

    // #region Restrictions
    public getRestrictions(): Observable<Restriction> {
        return this.recordRestrictionService.getRestrictions();
    }
    // #endregion

    // #region Utils
    public getAutocompleteFieldKey(
        fieldDefinition: BaseNestedFieldDefinition,
        subFieldDottedKey?: string
    ): string | null {
        // ENG-2284 autocompletable fields knowledge hardcoded in frontend
        /*
            Not all fields can be autocompleted. These restrictions are stated in the documentation:

            * Preconfigured fields
                * Global
                * Descriptive.Keywords.Keyword
            * Textual fields (SimpleField, EnumField) from these families
                * Descriptive
                * Administrative
                * Technical
                * Dynamic

            There's an open issue to expose this information directly through the fielddefinitions:
            https://mediahaven.atlassian.net/browse/ENG-2284
        */

        if (!this.allowedFamilies.includes(fieldDefinition.Family)) {
            return null;
        }

        switch (fieldDefinition.Type) {
            case FieldType.SimpleField:
                return fieldDefinition.Searchable ? fieldDefinition.DottedKey : null;

            case FieldType.ListField:
                return this.getListFieldAutocompleteFieldKey(fieldDefinition);

            case FieldType.MultiItemField:
                return this.getMultiItemAutocompleteFieldKey(fieldDefinition, subFieldDottedKey);

            // TODO: To be expanded for ComplexFields

            default:
                return null;
        }
    }

    public getListFieldAutocompleteFieldKey(fieldDefinition: BaseNestedFieldDefinition): string | null {
        if (!fieldDefinition.Children || fieldDefinition.Children.length === 0) {
            return null;
        }

        return this.getAutocompleteFieldKey(fieldDefinition.Children[0]);
    }

    public getMultiItemAutocompleteFieldKey(
        fieldDefinition: BaseNestedFieldDefinition,
        subFieldDottedKey?: string
    ): string | null {
        if (!fieldDefinition.Children || !subFieldDottedKey) {
            return null;
        }

        const subField: BaseNestedFieldDefinition | undefined = fieldDefinition.Children.find(
            child => child.DottedKey !== subFieldDottedKey
        );

        return subField ? this.getAutocompleteFieldKey(subField) : null;
    }

    // #endregion
}
